/**
 * Historia oftalmológica (4/11/2026): formatos, etiquetas y cuentas de la
 * pantalla. Las cuentas son las mismas que hace el servidor
 * (backend/services/oftalmo_calculos.py); aquí se repiten para enseñar el
 * resultado mientras se escribe, antes de guardar.
 */

export const COLOR_OJO = { OD: '#DC2626', OI: '#2563EB' };
export const OJOS = [['od', 'OD'], ['oi', 'OI']];

export const CATEGORIAS = {
  oct_macula: 'OCT macular',
  oct_nervio: 'OCT de nervio óptico',
  retinografia: 'Retinografía',
  autofluorescencia: 'Autofluorescencia',
  angiografia: 'Angiografía / OCT-A',
  cornea: 'Córnea (lámpara)',
  topografia: 'Topografía / tomografía corneal',
  segmento_anterior: 'Segmento anterior',
  parpados: 'Párpados / oculoplastia (fotos)',
  campo_visual: 'Campo visual (informe)',
  biometria: 'Biometría (informe)',
  otra: 'Otra',
};

// Los valores que se apuntan con cada tipo de imagen.
export const VALORES_DE_CATEGORIA = {
  oct_macula: [['cst', 'Grosor central (µm)'], ['volumen', 'Volumen (mm³)']],
  oct_nervio: [['cfnr', 'CFNR medio (µm)'], ['cd', 'Excavación (C/D)']],
  retinografia: [['cd', 'Excavación (C/D)']],
  topografia: [['kmax', 'Kmax (D)'], ['k1', 'K1 (D)'], ['k2', 'K2 (D)'], ['astig', 'Astig. corneal (D)'],
    ['eje', 'Eje (°)'], ['paqui_min', 'Paquimetría mínima (µm)']],
};

export const CLASES_REFRACCION = {
  habitual: 'Habitual (su graduación)',
  autorrefractometro: 'Autorrefractómetro',
  subjetiva: 'Subjetiva',
  cicloplejica: 'Ciclopléjica',
  objetivo: 'Objetivo previsto',
  postoperatoria: 'Postoperatoria (residual)',
};

export const METODOS_PIO = ['Goldmann', 'Aire (no contacto)', 'iCare', 'Perkins', 'Tono-Pen'];
export const TECNICAS_REFRACTIVA = ['FemtoLASIK', 'LASIK', 'PRK', 'LASEK', 'SMILE', 'ICL', 'Lensectomía refractiva'];
export const TIPOS_LENTE = ['Monofocal', 'Monofocal tórica', 'Monofocal +', 'EDOF', 'EDOF tórica', 'Trifocal',
  'Trifocal tórica', 'Fáquica (ICL)'];
export const FORMULAS = ['Barrett Universal II', 'Kane', 'Hill-RBF', 'EVO', 'Haigis', 'SRK/T', 'Hoffer Q', 'Holladay 1'];
export const FARMACOS_INTRAVITREOS = ['Aflibercept 2 mg', 'Aflibercept 8 mg', 'Faricimab 6 mg', 'Ranibizumab 0,5 mg',
  'Bevacizumab 1,25 mg', 'Implante de dexametasona 0,7 mg', 'Pegcetacoplán 15 mg'];

/** Un número de lo que se escribe («-2,25», «+1.5»), o null. */
export function num(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const t = String(v).trim().replace(',', '.').replace(/\s/g, '');
  if (!t) return null;
  const x = Number(t);
  return Number.isFinite(x) ? x : null;
}

/** Dioptrías con su signo y dos decimales, a la española: «+1,25», «−0,50». */
export function dpt(v, { signo = true } = {}) {
  const x = num(v);
  if (x === null) return '—';
  const abs = Math.abs(x).toFixed(2).replace('.', ',');
  if (!signo) return abs;
  if (x === 0) return '0,00';
  return `${x > 0 ? '+' : '−'}${abs}`;
}

export function dec(v, d = 1) {
  const x = num(v);
  return x === null ? '—' : x.toFixed(d).replace('.', ',');
}

/** Agudeza visual decimal: 0,05 con dos decimales; 0,8 con uno. */
export function av(v) {
  const x = num(v);
  if (x === null) return '—';
  return x.toFixed(x > 0 && x < 0.1 ? 2 : 1).replace('.', ',');
}

export function ee(esf, cil) {
  const e = num(esf);
  if (e === null) return null;
  return Math.round((e + (num(cil) || 0) / 2) * 1000) / 1000;
}

/** «−4,50 −1,00 × 175°» (y la AV si la hay). */
export function refraccionTexto(o) {
  if (!o || num(o.esf) === null) return '—';
  let t = dpt(o.esf);
  if (num(o.cil) !== null && num(o.cil) !== 0) t += ` ${dpt(o.cil)} × ${Math.round(num(o.eje) || 0)}°`;
  else t += ' esf';
  if (num(o.add) !== null) t += ` · Add ${dpt(o.add)}`;
  return t;
}

export function ablacionMunnerlyn(esf, cil, zona = 6.5) {
  const e = num(esf);
  if (e === null) return null;
  const masMiope = Math.min(e, e + (num(cil) || 0));
  if (masMiope >= 0) return null;
  const zo = num(zona) || 6.5;
  return Math.round(Math.abs(masMiope) * zo * zo / 3 * 10) / 10;
}

export function seguridadLaser(paquimetria, colgajo, ablacion) {
  const p = num(paquimetria);
  const f = num(colgajo) || 0;
  const a = num(ablacion);
  if (p === null || a === null || p <= 0) return { lecho: null, pta: null, avisos: [] };
  const lecho = Math.round(p - f - a);
  const pta = Math.round((f + a) / p * 1000) / 10;
  const avisos = [];
  if (f > 0 && lecho < 300) avisos.push(`Lecho residual de ${lecho} µm (< 300 µm)`);
  if (pta >= 40) avisos.push(`PTA del ${String(pta).replace('.', ',')} % (≥ 40 %)`);
  return { lecho, pta, avisos };
}

/** Área (mm²) y diámetro mayor (mm) del contorno, con la referencia. */
export function medirLesion(medicion, ancho, alto) {
  const out = { area_mm2: null, diametro_mm: null, area_pct: null };
  if (!medicion || !ancho || !alto) return out;
  const pts = (medicion.puntos || []).map(([x, y]) => [x / 100 * ancho, y / 100 * alto]);
  if (pts.length < 3) return out;
  let s = 0;
  for (let i = 0; i < pts.length; i += 1) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % pts.length];
    s += x1 * y2 - x2 * y1;
  }
  const areaPx = Math.abs(s) / 2;
  out.area_pct = Math.round(areaPx / (ancho * alto) * 100000) / 1000;
  const ref = medicion.referencia;
  if (!ref || !ref.a || !ref.b || !num(ref.mm)) return out;
  const largo = Math.hypot((ref.a[0] - ref.b[0]) / 100 * ancho, (ref.a[1] - ref.b[1]) / 100 * alto);
  if (largo < 1) return out;
  const mmPx = num(ref.mm) / largo;
  out.area_mm2 = Math.round(areaPx * mmPx * mmPx * 100) / 100;
  let diam = 0;
  for (let i = 0; i < pts.length; i += 1) {
    for (let j = i + 1; j < pts.length; j += 1) {
      diam = Math.max(diam, Math.hypot(pts[i][0] - pts[j][0], pts[i][1] - pts[j][1]));
    }
  }
  out.diametro_mm = Math.round(diam * mmPx * 100) / 100;
  return out;
}

export function fechaCorta(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y.slice(2)}`;
}

export function aTiempo(iso) {
  return new Date(`${String(iso).slice(0, 10)}T12:00:00`).getTime();
}

/** Etiqueta del eje de fechas: mes/año, o día/mes si la serie es corta. */
export function tickFecha(t, corta = false) {
  const d = new Date(t);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return corta ? `${String(d.getDate()).padStart(2, '0')}/${mm}` : `${mm}/${String(d.getFullYear()).slice(2)}`;
}

/** Último registro de un tipo (y que cumpla `con`), por fecha. */
export function ultimo(registros, tipo, con = () => true) {
  const xs = (registros || []).filter((r) => r.tipo === tipo && con(r));
  if (!xs.length) return null;
  return xs.reduce((a, b) => ((b.fecha + (b.created_at || '')) >= (a.fecha + (a.created_at || '')) ? b : a));
}

/** Una tabla como CSV (separado por «;», como lo abre Excel en español). */
export function aCsv(filas, columnas) {
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const t = typeof v === 'number' ? String(v).replace('.', ',') : String(v);
    return /[;"\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  const cab = columnas.map(([, t]) => esc(t)).join(';');
  const cuerpo = filas.map((f) => columnas.map(([k]) => esc(f[k])).join(';'));
  return `﻿${[cab, ...cuerpo].join('\n')}`;
}

export function descargar(nombre, contenido, tipo = 'text/csv;charset=utf-8') {
  const blob = contenido instanceof Blob ? contenido : new Blob([contenido], { type: tipo });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
